import { useMemo } from 'react';
import { sortPitchSongs } from 'fym-shared/src/music24/outreach/pitchSong';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { catalogArtworkSrc, CatalogArtworkSource } from '../../../smartLinks/catalog/catalogArtwork';

export interface ReleaseOption {
  catalogItemUuid: string;
  title: string;
  coverUrl: string | null;
  releaseDate: string | null;
  /** An unreleased catalog row (fym-shared `pitchSong.ts` reads it). */
  unreleased: boolean;
}

interface CatalogRow extends CatalogArtworkSource {
  uuid: string;
  title: string;
  releaseDate: string | null;
  kind?: CatalogItemKind;
}

/**
 * One song of the pickers. The cover goes through `catalogArtworkSrc`: an uploaded file has a
 * relative url and needs the API address in front of it.
 */
export function toReleaseOption(item: CatalogRow): ReleaseOption {
  return {
    catalogItemUuid: item.uuid,
    title: item.title,
    coverUrl: catalogArtworkSrc(item, 160) ?? null,
    releaseDate: item.releaseDate,
    unreleased: item.kind === CatalogItemKind.unreleased,
  };
}

/**
 * The catalog songs of the artist, for the song pickers of Find and the builder (frames c1, e), in
 * pitch order (fym-shared `pitchSong.ts`): the next upcoming song first, then the songs of the last
 * 14 days, then the unreleased songs with no date, then the older songs. The first option is the
 * song to pitch.
 */
export function useReleaseOptions(accountId: number) {
  const { data, isLoading } = useGetCatalogQuery({ accountId });
  const options = useMemo<ReleaseOption[]>(
    () => sortPitchSongs((data?.items ?? []).map(toReleaseOption), new Date()),
    [data]
  );
  return { options, isLoading };
}
